// Soll des Mietkontos: aus Mietverhältnissen (MV/SEV) bzw. Hausgeld (WEG).
// Keine neue Tabelle – Quelle sind mieter (miet_historie, Mietbeginn/-ende) und einheit_eigentuemer.

import { aktuellerStand, gesamtmiete, leseStand } from '../mieter/mietstaende';
import { istWohnend } from '../dashboard/leerstand';
import { monateZwischen, monatsEnde, runde, tagImMonat, tageImMonat } from './datum';
import type { SollPosten } from './typen';

export interface MietverhaeltnisRoh {
  id: string;
  einheit_id: string | null;
  status: string | null;
  kaltmiete: number | string | null;
  nebenkosten_vorauszahlung: number | string | null;
  heizkosten: number | string | null;
  weitere_kosten: number | string | null;
  miet_historie: unknown;
  mietbeginn: string | null;
  mietende: string | null;
  miete_anteilig_beginn?: boolean | null;
  miete_anteilig_ende?: boolean | null;
  miete_faellig_tag?: number | string | null;
  anfangssaldo_aktiv?: boolean | null;
  anfangssaldo_betrag?: number | string | null;
  anfangssaldo_faelligkeit?: string | null;
}

export interface HausgeldRoh {
  einheit_id: string;
  hausgeld_vorschuss: number | string | null;
  ruecklage_vorschuss: number | string | null;
  vertrag_ab: string | null;
  vertrag_bis: string | null;
}

const zahl = (v: unknown): number => {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v.replace(',', '.')) : NaN;
  return Number.isFinite(n) ? n : 0;
};

const STANDARD_FAELLIG_TAG = 3;

/** Monatliche Gesamtmiete am Stichtag: Stand aus miet_historie, sonst die Spiegelfelder. */
export function gesamtmieteAm(m: MietverhaeltnisRoh, stichtag: string): number {
  const historie = Array.isArray(m.miet_historie) ? m.miet_historie.map((r, i) => leseStand(r, i)) : [];
  const stand = aktuellerStand(historie, stichtag);
  if (stand) return gesamtmiete(stand);
  return zahl(m.kaltmiete) + zahl(m.nebenkosten_vorauszahlung) + zahl(m.heizkosten) + zahl(m.weitere_kosten);
}

/**
 * Soll-Posten eines Mietverhältnisses für alle Monate von `start` bis `bis`.
 * - Ein Monat zählt, wenn das Mietverhältnis ihn berührt (Beginn ≤ Monatsende, Ende leer oder ≥ Monatsanfang).
 *   Ohne Mietbeginn zählt nur, wer heute wohnt.
 * - Betrag = Gesamtmiete laut Mietstand, der zu Monatsbeginn (bzw. Mietbeginn) gilt.
 * - Anteilig im ersten/letzten Monat, wenn im Mietvertrag so vermerkt (taggenau).
 * - Fällig am vereinbarten Tag (Standard: 3.), nie vor Mietbeginn.
 * - Anfangssaldo (Altrückstand) als eigener Posten zur angegebenen Fälligkeit.
 * Mieten werden monatlich angesetzt; andere Zahlungsintervalle (Quartal usw.) sind noch nicht abgebildet.
 */
export function sollFuerMietverhaeltnis(m: MietverhaeltnisRoh, start: string, bis: string): SollPosten[] {
  const beginn = m.mietbeginn?.slice(0, 10) || null;
  const ende = m.mietende?.slice(0, 10) || null;
  if (!beginn && !istWohnend(m.status)) return [];
  const faelligTag = zahl(m.miete_faellig_tag) || STANDARD_FAELLIG_TAG;
  const posten: SollPosten[] = [];

  for (const monat of monateZwischen(start, bis)) {
    const monatEnde = monatsEnde(monat);
    if (beginn && beginn > monatEnde) continue;
    if (ende && ende < monat) continue;
    const stichtag = beginn && beginn > monat ? beginn : monat;
    let betrag = gesamtmieteAm(m, stichtag);
    if (betrag <= 0) continue;

    const tage = tageImMonat(monat);
    const vonTag = m.miete_anteilig_beginn && beginn && beginn > monat ? Number(beginn.slice(8, 10)) : 1;
    const bisTag = m.miete_anteilig_ende && ende && ende < monatEnde ? Number(ende.slice(8, 10)) : tage;
    if (vonTag !== 1 || bisTag !== tage) betrag = (betrag * Math.max(0, bisTag - vonTag + 1)) / tage;

    let faellig = tagImMonat(monat, faelligTag);
    if (beginn && faellig < beginn) faellig = beginn;
    posten.push({ monat, faellig, betrag: runde(betrag), art: 'miete' });
  }

  const alt = zahl(m.anfangssaldo_betrag);
  if (m.anfangssaldo_aktiv && alt !== 0) {
    const faellig = (m.anfangssaldo_faelligkeit?.slice(0, 10) || beginn || start);
    const monat = faellig < start ? start : `${faellig.slice(0, 7)}-01`;
    if (monat <= bis) posten.push({ monat, faellig: faellig < start ? start : faellig, betrag: runde(alt), art: 'anfangssaldo' });
  }
  return posten.sort((a, b) => a.faellig.localeCompare(b.faellig));
}

/** Hausgeld (inkl. Rücklage) einer WEG-Einheit, monatlich am 1., innerhalb der Vertragslaufzeit. */
export function sollFuerHausgeld(h: HausgeldRoh, start: string, bis: string): SollPosten[] {
  const betrag = runde(zahl(h.hausgeld_vorschuss) + zahl(h.ruecklage_vorschuss));
  if (betrag <= 0) return [];
  const ab = h.vertrag_ab?.slice(0, 10) || null;
  const ende = h.vertrag_bis?.slice(0, 10) || null;
  return monateZwischen(start, bis)
    .filter(monat => (!ab || ab <= monatsEnde(monat)) && (!ende || ende >= monat))
    .map(monat => ({ monat, faellig: tagImMonat(monat, 1), betrag, art: 'hausgeld' as const }));
}
